import type { DesignBody, DesignNode } from '@repo/api-contracts';

/** 디자인 사본의 텍스트 노드 한 개 — 관리자가 제작 전에 확인하는 문구·폰트·잉크. */
export interface QuoteDesignText {
  readonly nodeId: string;
  /** 편집영역 컴포넌트(면) id. */
  readonly componentId: string;
  readonly text: string;
  readonly fontId: string;
  readonly inkColorId: string;
}

export interface QuoteDesignContents {
  /** 면 순서 → 레이어 순서(그룹 안은 자식 순서)대로. */
  readonly texts: readonly QuoteDesignText[];
  /** 쓰인 폰트 id — 처음 나온 순서로 한 번씩. */
  readonly fontIds: readonly string[];
  /** 쓰인 잉크 id — 처음 나온 순서로 한 번씩. */
  readonly inkColorIds: readonly string[];
}

const textsOf = (componentId: string, nodes: readonly DesignNode[]): QuoteDesignText[] =>
  nodes.flatMap((node): QuoteDesignText[] => {
    if (node.type === 'group') return textsOf(componentId, node.children);
    if (node.type !== 'text') return [];
    return [
      {
        nodeId: node.id,
        componentId,
        text: node.text,
        fontId: node.fontId,
        inkColorId: node.inkColorId,
      },
    ];
  });

const distinct = (values: readonly string[]): string[] => [...new Set(values)];

/**
 * 견적 디자인 사본(design_body)에서 텍스트·폰트·잉크 목록을 뽑는다(견적 설계 §6-2 "복사본에서 읽는다").
 * 렌더러(Phase 7)와 무관한 순수 함수다. 사본에는 폰트·잉크 이름이 없어 식별자만 돌려준다.
 */
export const extractQuoteDesignContents = (body: DesignBody): QuoteDesignContents => {
  const texts = body.components.flatMap((component) =>
    textsOf(component.componentId, component.layers),
  );
  return {
    texts,
    fontIds: distinct(texts.map((text) => text.fontId)),
    inkColorIds: distinct(texts.map((text) => text.inkColorId)),
  };
};
